import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Pencil, CalendarDays } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import type { Projeto, ProjetoEtapa, Profile, Tarefa } from '../../types';
import { calcularProgresso } from '../../src/lib/projetos/progresso';
import { calcularEquipe } from '../../src/lib/projetos/equipe';
import { configDaEtapa } from '../../src/lib/projetos/etapas';
import EtapaStepper from './EtapaStepper';
import ProjetoFormModal from './ProjetoFormModal';
import ProjetoAtividadeTimeline from './ProjetoAtividadeTimeline';
import EquipeAvatares from './EquipeAvatares';
import ProjetoTarefasPanel, { type FiltroTarefas } from './ProjetoTarefasPanel';
import ProjetoVisaoGeral from './ProjetoVisaoGeral';
import ProjetoDocumentacaoPanel from './ProjetoDocumentacaoPanel';
import { faltaDocumentacaoAoConcluir } from '../../src/lib/projetos/documentos';
import type { ProjetoDocumento } from '../../types';

type Aba = 'visao' | 'tarefas' | 'documentacao' | 'atividade';

const ProjetoDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const podeGerir = profile?.role === 'adm' || profile?.role === 'gerente';

  const [projeto, setProjeto] = useState<Projeto | null>(null);
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  const [users, setUsers] = useState<Profile[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState<Aba>('visao');
  const [editando, setEditando] = useState(false);
  const [filtroTarefas, setFiltroTarefas] = useState<FiltroTarefas>('todas');
  // Sobe do painel para o contador da aba e para o alerta do cabeçalho acompanharem sem
  // uma segunda consulta.
  const [documentos, setDocumentos] = useState<ProjetoDocumento[]>([]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setCarregando(true);
      const { data: proj } = await supabase.from('projetos')
        .select('*, responsavel:profiles!projetos_responsavel_id_fkey(id, full_name, avatar_url)')
        .eq('id', id).single();

      // Duas consultas em vez de um `or`: as tarefas do projeto e, depois, as subtarefas delas.
      // Subtarefa não carrega projeto_id — ela pertence ao projeto pela mãe.
      const { data: maes } = await supabase.from('tarefas').select('*').eq('projeto_id', id);
      const idsMae = (maes || []).map(t => t.id);
      const { data: filhas } = idsMae.length
        ? await supabase.from('tarefas').select('*').in('parent_id', idsMae)
        : { data: [] as Tarefa[] };
      // Sem consumidores: com eles a lista passa de 1.000 linhas, o Supabase corta, e quem fica
      // de fora some da atividade ("Alguém") e do seletor de responsável.
      const { data: perfis } = await supabase.from('profiles').select('*').neq('role', 'consumidor');

      // Só o suficiente para o contador da aba e o aviso de "concluído sem documentação"
      // aparecerem SEM a pessoa precisar abrir a aba — se o alerta só surgisse lá dentro,
      // quem não abre nunca saberia que a documentação está faltando. O conteúdo completo
      // (markdown, autor, anexos) o painel carrega quando é montado.
      const { data: docs } = await supabase.from('projeto_documentos')
        .select('id, tipo').eq('projeto_id', id);

      setDocumentos((docs || []) as ProjetoDocumento[]);
      setProjeto((proj as Projeto) || null);
      setTarefas([...(maes || []), ...(filhas || [])] as Tarefa[]);
      setUsers((perfis || []) as Profile[]);
      setCarregando(false);
    })();
  }, [id]);

  const progresso = useMemo(() => calcularProgresso(tarefas), [tarefas]);
  const equipe = useMemo(
    () => calcularEquipe({ gestorId: projeto?.responsavel_id, tarefas }),
    [projeto?.responsavel_id, tarefas],
  );
  // Rejeitada/cancelada fica fora do contador da aba, como fica fora do progresso — antes a aba
  // dizia 16 e o cabeçalho 15, sem explicar a diferença.
  const maesValidas = useMemo(
    () => tarefas.filter(t => t.parent_id == null && !['rejeitado', 'cancelado'].includes(t.solicitacao_status || t.status || '')),
    [tarefas],
  );
  const abrirTarefas = (filtro: FiltroTarefas) => {
    setFiltroTarefas(filtro);
    setAba('tarefas');
  };

  const salvarEtapa = async (etapa: ProjetoEtapa) => {
    if (!projeto) return;
    const anterior = projeto;
    setProjeto({ ...projeto, etapa }); // otimista: o stepper responde na hora
    const { error } = await supabase.from('projetos')
      .update({ etapa, updated_at: new Date().toISOString() }).eq('id', projeto.id);
    if (error) setProjeto(anterior); // desfaz se a RLS recusar
  };

  if (carregando) {
    return <div className="p-6 flex items-center gap-2 text-gray-500"><Loader2 size={16} className="animate-spin" /> Carregando...</div>;
  }
  if (!projeto) {
    return (
      <div className="p-6">
        <p className="text-gray-500 dark:text-gray-400">Projeto não encontrado.</p>
        <Link to="/projetos" className="text-primary text-sm hover:underline">Voltar para Projetos</Link>
      </div>
    );
  }

  const cfg = configDaEtapa(projeto.etapa);
  const faltaDocumentacao = faltaDocumentacaoAoConcluir(projeto.etapa, documentos);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <Link to="/projetos" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary mb-3">
        <ArrowLeft size={14} /> Projetos
      </Link>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">{projeto.nome}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center gap-1.5">
                Responsáveis: <EquipeAvatares equipe={equipe} users={users} size={20} />
              </span>
              {projeto.prazo && (
                <span className="inline-flex items-center gap-1">
                  <CalendarDays size={12} /> {new Date(`${projeto.prazo.slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR')}
                </span>
              )}
              {projeto.tags.map(t => (
                <span key={t} className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{t}</span>
              ))}
            </div>
          </div>
          {podeGerir && (
            <button onClick={() => setEditando(true)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary">
              <Pencil size={14} /> Editar
            </button>
          )}
        </div>

        <EtapaStepper etapa={projeto.etapa} editavel={podeGerir} onChange={salvarEtapa} />

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>{progresso.concluidas} de {progresso.total} tarefas concluídas</span>
            <span className="font-bold text-gray-700 dark:text-gray-200">{progresso.pct}%</span>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2.5">
            <div className="h-2.5 rounded-full transition-all duration-700"
              style={{ width: `${Math.max(1, progresso.pct)}%`, background: cfg.cor }} />
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        {([
          ['visao', 'Visão Geral'],
          ['tarefas', `Tarefas (${maesValidas.length})`],
          ['documentacao', documentos.length ? `Documentação (${documentos.length})` : 'Documentação'],
          ['atividade', 'Atividade'],
        ] as [Aba, string][])
          .map(([valor, rotulo]) => (
            <button key={valor} onClick={() => setAba(valor)}
              aria-label={valor === 'documentacao' && faltaDocumentacao ? `${rotulo} — projeto concluído sem documentação` : undefined}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                aba === valor ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}>
              {rotulo}
              {/* Projeto fechado sem nada escrito: o ponto âmbar é o sinal visual da
                  pendência; o `aria-label` acima carrega a mesma informação para quem usa
                  leitor de tela, já que `title` sozinho não é lido de forma confiável. */}
              {valor === 'documentacao' && faltaDocumentacao && (
                <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-500 align-middle"
                  title="Projeto concluído sem documentação" />
              )}
            </button>
          ))}
      </div>

      {aba === 'visao' && (
        <ProjetoVisaoGeral projeto={projeto} tarefas={tarefas} users={users} equipe={equipe}
          progresso={progresso} documentos={documentos.length} faltaDocumentacao={faltaDocumentacao}
          onAbrirTarefas={abrirTarefas} onAbrirAba={setAba} />
      )}

      {aba === 'tarefas' && <ProjetoTarefasPanel tarefas={tarefas} users={users} filtro={filtroTarefas} onFiltro={setFiltroTarefas} />}

      {aba === 'documentacao' && (
        <ProjetoDocumentacaoPanel
          projetoId={projeto.id}
          etapa={projeto.etapa}
          users={users}
          onMudou={setDocumentos}
        />
      )}

      {aba === 'atividade' && (
        <ProjetoAtividadeTimeline projetoId={projeto.id} projetoNome={projeto.nome} tarefas={tarefas} users={users} />
      )}

      {editando && (
        <ProjetoFormModal projeto={projeto} users={users} onClose={() => setEditando(false)} onSaved={setProjeto} />
      )}
    </div>
  );
};

export default ProjetoDetailView;
